import { isMatchingRoutePath } from "@/common/helpers/route";
import type { NavigationItem } from "@/config/navigation/types";
import type { ElementType } from "react";
import type { PaletteCommand } from "../types";

const PATH_SEPARATOR = " › ";

export function collectNavigationCommands(
  items: NavigationItem[],
  parentTitles: string[] = [],
  parentIcon?: ElementType,
): PaletteCommand[] {
  return items.flatMap((item) => {
    const title = item.title();
    const Icon = item.icon ?? parentIcon;
    const isNavigable = item.navigable !== false && !!item.routePath && !!item.component;

    const ownCommand: PaletteCommand[] = isNavigable
      ? [
          {
            id: item.id,
            title,
            subtitle: parentTitles.join(PATH_SEPARATOR) || undefined,
            icon: Icon && <Icon />,
            keywords: parentTitles,
            to: item.routePath!,
          },
        ]
      : [];

    return [...ownCommand, ...collectNavigationCommands(item.children ?? [], [...parentTitles, title], Icon)];
  });
}

export type ModuleCommands = {
  sectionItem?: NavigationItem;
  sectionCommands: PaletteCommand[];
  moduleCommands: PaletteCommand[];
};

export function collectModuleCommands(moduleItem: NavigationItem | undefined, location: string): ModuleCommands {
  const result: ModuleCommands = { sectionCommands: [], moduleCommands: [] };
  if (!moduleItem) return result;

  const moduleTitle = moduleItem.title();
  const visit = (item: NavigationItem, depth: number) => {
    const commands = toPaletteCommands(item, moduleTitle);
    const isOnCurrentPath = depth > 0 && !!item.routePath && isMatchingRoutePath(item.routePath, location);

    if (isOnCurrentPath && commands.length > 0) {
      result.sectionItem = item;
      result.sectionCommands.push(...commands);
    } else {
      result.moduleCommands.push(...commands);
    }

    item.children?.forEach((child) => visit(child, depth + 1));
  };
  visit(moduleItem, 0);

  return result;
}

function toPaletteCommands(item: NavigationItem, moduleTitle: string): PaletteCommand[] {
  return (item.commands ?? []).map((command): PaletteCommand => {
    const Icon = command.icon ?? item.icon;

    return {
      id: command.id,
      title: command.title(),
      icon: Icon && <Icon />,
      keywords: [moduleTitle],
      to: command.to,
    };
  });
}
